import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { usePlatform } from './PlatformContext';

const ThemeContext = createContext();

// Default commercial theme values
const defaultTheme = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  textColor: '#1a202c',
  textMutedColor: '#718096',
  backgroundColor: '#f7fafc',
  buttonColor: '#3182ce',
  buttonHoverColor: '#2c5aa0',
  buttonTextColor: '#ffffff',
  buttonSecondaryColor: '#edf2f7',
  buttonSecondaryTextColor: '#374151',
  cardBackgroundColor: '#ffffff',
  borderColor: '#e2e8f0',
  accentColor: '#38b2ac',
  sidebarColor: '#2d3748',
  sidebarTextColor: '#e2e8f0',
  sidebarIconColor: '#cbd5e0',
  sidebarActiveColor: '#3182ce',
  sidebarActiveTextColor: '#ffffff',
  sidebarActiveIconColor: '#ffffff',
  headerColor: '#ffffff',
  headerTextColor: '#2d3748',
  linkColor: '#3182ce',
  linkHoverColor: '#2c5aa0',
  inputBackgroundColor: '#ffffff',
  inputBorderColor: '#cbd5e0',
  inputFocusColor: '#3182ce',
  inputTextColor: '#1a202c',
  inputPlaceholderColor: '#a0aec0',
  filterTextColor: '#1a202c',
  filterLabelColor: '#4a5568',
  successColor: '#38a169',
  warningColor: '#d69e2e',
  errorColor: '#e53e3e',
  infoColor: '#3182ce',
  shadowColor: '#0000000a',
  hoverBackgroundColor: '#f7fafc'
};

// Helper functions for localStorage theme management (per-platform)
const getStoredTheme = (platformId) => {
  try {
    // Store per-platform themes with a key like 'app_theme_plt_123' or 'app_theme_all'
    const key = `app_theme_${platformId || 'all'}`;
    const stored = localStorage.getItem(key);
    console.log('[ThemeContext] getStoredTheme for platform:', platformId, 'found:', !!stored);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error reading stored theme:', error);
    return null;
  }
};

const storeTheme = (theme, platformId) => {
  try {
    // Store per-platform themes
    const key = `app_theme_${platformId || 'all'}`;
    localStorage.setItem(key, JSON.stringify(theme));
    console.log('[ThemeContext] storeTheme for platform:', platformId, 'saved to:', key);
  } catch (error) {
    console.error('Error storing theme:', error);
  }
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { user, token, selectedPlatform } = useAuth();
  const { currentPlatform } = usePlatform();
  const [theme, setTheme] = useState(defaultTheme); // Start with default, load from backend
  const [loading, setLoading] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [initialThemeApplied, setInitialThemeApplied] = useState(false);
  const { backendUrl } = useAuth();

  // Track authentication state changes
  useEffect(() => {
    if (!user && !token) {
      setIsAuthenticating(false);
    } else if (token && !user) {
      setIsAuthenticating(true);
    } else if (token && user) {
      setIsAuthenticating(false);
    }
  }, [user, token]);

  // Apply initial theme only after determining authentication state
  useEffect(() => {
    if (!initialThemeApplied && !isAuthenticating) {
      // If there's a token, wait for platform theme to load from backend
      // If no token, apply default theme immediately
      if (!token) {
        applyThemeToCSS(defaultTheme);
        setInitialThemeApplied(true);
      }
    }
  }, [token, isAuthenticating, initialThemeApplied]);

  // Load theme when user, token, or selected platform changes
  useEffect(() => {
    if (user && token && user.email && !isAuthenticating) {
      // Only load theme if we have a complete user object and not in transition
      console.log('[ThemeContext] Loading theme for user:', user.email, 'platform:', selectedPlatform);
      loadTheme();
      if (!initialThemeApplied) {
        setInitialThemeApplied(true);
      }
    } else if (!token && !isAuthenticating) {
      // Apply default theme when user is logged out
      console.log('[ThemeContext] User logged out, applying default theme');
      setTheme(defaultTheme);
      applyThemeToCSS(defaultTheme);
      setLoading(false);
    }
  }, [user, token, selectedPlatform, isAuthenticating, initialThemeApplied]);

  // Apply theme to CSS variables when theme changes
  useEffect(() => {
    applyThemeToCSS(theme, true); // Apply instantly without transitions to prevent flash
  }, [theme]);

  const loadTheme = async () => {
    // Determine platform ID for theme loading (move outside try-catch)
    let platformId = 'all'; // Default to "all" for default theme
    
    if (selectedPlatform && selectedPlatform !== 'all') {
      platformId = selectedPlatform;
    } else if (currentPlatform && currentPlatform.id !== 'all') {
      platformId = currentPlatform.id;
    }

    console.log('[ThemeContext] loadTheme - selectedPlatform:', selectedPlatform, 'resolved platformId:', platformId);

    try {
      const url = `${backendUrl}/api/theme/settings${platformId ? `?platform_id=${platformId}` : ''}`;
      console.log('[ThemeContext] Fetching theme from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[ThemeContext] Theme loaded from backend:', data.theme ? 'Platform-specific theme' : 'Default theme', 'for platform:', platformId);
        const loadedTheme = data.theme ? { ...defaultTheme, ...data.theme } : defaultTheme;
        setTheme(loadedTheme); // Setting theme will trigger useEffect to apply CSS
        storeTheme(loadedTheme, platformId); // Store with platform ID
      } else {
        console.error('[ThemeContext] Failed to load theme, status:', response.status);
        // On error, try localStorage for this platform, then default
        const cachedTheme = getStoredTheme(platformId);
        const fallbackTheme = cachedTheme || defaultTheme;
        console.log('[ThemeContext] Using fallback theme:', cachedTheme ? 'cached' : 'default');
        setTheme(fallbackTheme); // Setting theme will trigger useEffect to apply CSS
      }
    } catch (error) {
      console.error('[ThemeContext] Error loading theme:', error);
      // On error, try localStorage for this platform, then default
      const cachedTheme = getStoredTheme(platformId);
      const fallbackTheme = cachedTheme || defaultTheme;
      console.log('[ThemeContext] Using fallback theme after error:', cachedTheme ? 'cached' : 'default');
      setTheme(fallbackTheme); // Setting theme will trigger useEffect to apply CSS
    } finally {
      setLoading(false);
    }
  };

  const saveTheme = async (newTheme) => {
    try {
      // Determine platform ID for theme saving
      let platformId = 'all'; // Default to "all" for default theme
      
      if (selectedPlatform && selectedPlatform !== 'all') {
        platformId = selectedPlatform;
      } else if (currentPlatform && currentPlatform.id !== 'all') {
        platformId = currentPlatform.id;
      }

      console.log('[ThemeContext] saveTheme for platform:', platformId);

      const url = `${backendUrl}/api/theme/settings${platformId ? `?platform_id=${platformId}` : ''}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ theme: newTheme })
      });

      if (response.ok) {
        console.log('[ThemeContext] Theme saved successfully to backend for platform:', platformId);
        setTheme(newTheme); // Setting theme will trigger useEffect to apply CSS
        storeTheme(newTheme, platformId); // Store with platform ID
        return { success: true };
      } else {
        console.error('[ThemeContext] Failed to save theme to backend');
        return { success: false, error: 'Failed to save theme' };
      }
    } catch (error) {
      console.error('[ThemeContext] Error saving theme:', error);
      return { success: false, error: error.message };
    }
  };

  const resetTheme = () => {
    setTheme(defaultTheme);
    saveTheme(defaultTheme);
  };

  const getCurrentPlatformName = () => {
    if (selectedPlatform && selectedPlatform !== 'all') {
      return currentPlatform?.name || 'Selected Platform';
    } else if (selectedPlatform === 'all' || !selectedPlatform) {
      return 'All Platforms (Default)';
    }
    return 'Current Platform';
  };

  const applyThemeToCSS = (themeData, instant = false) => {
    const root = document.documentElement;
    
    // Disable transitions temporarily for instant theme application
    if (instant) {
      root.style.setProperty('transition', 'none');
    }
    
    // Apply CSS custom properties
    root.style.setProperty('--font-family', themeData.fontFamily);
    root.style.setProperty('--text-color', themeData.textColor);
    root.style.setProperty('--text-muted-color', themeData.textMutedColor);
    root.style.setProperty('--background-color', themeData.backgroundColor);
    root.style.setProperty('--button-color', themeData.buttonColor);
    root.style.setProperty('--button-hover-color', themeData.buttonHoverColor);
    root.style.setProperty('--button-text-color', themeData.buttonTextColor || '#ffffff');
    root.style.setProperty('--button-secondary-color', themeData.buttonSecondaryColor);
    root.style.setProperty('--button-secondary-text-color', themeData.buttonSecondaryTextColor || '#374151');
    root.style.setProperty('--card-background', themeData.cardBackgroundColor);
    root.style.setProperty('--border-color', themeData.borderColor);
    root.style.setProperty('--accent-color', themeData.accentColor);
    root.style.setProperty('--sidebar-color', themeData.sidebarColor);
    root.style.setProperty('--sidebar-text-color', themeData.sidebarTextColor);
    root.style.setProperty('--sidebar-icon-color', themeData.sidebarIconColor);
    root.style.setProperty('--sidebar-active-color', themeData.sidebarActiveColor);
    root.style.setProperty('--sidebar-active-text-color', themeData.sidebarActiveTextColor);
    root.style.setProperty('--sidebar-active-icon-color', themeData.sidebarActiveIconColor);
    root.style.setProperty('--header-color', themeData.headerColor);
    root.style.setProperty('--header-text-color', themeData.headerTextColor);
    root.style.setProperty('--link-color', themeData.linkColor);
    root.style.setProperty('--link-hover-color', themeData.linkHoverColor);
    root.style.setProperty('--input-background', themeData.inputBackgroundColor);
    root.style.setProperty('--input-border-color', themeData.inputBorderColor);
    root.style.setProperty('--input-focus-color', themeData.inputFocusColor);
    root.style.setProperty('--input-text-color', themeData.inputTextColor);
    root.style.setProperty('--input-placeholder-color', themeData.inputPlaceholderColor);
    root.style.setProperty('--filter-text-color', themeData.filterTextColor);
    root.style.setProperty('--filter-label-color', themeData.filterLabelColor);
    root.style.setProperty('--success-color', themeData.successColor);
    root.style.setProperty('--warning-color', themeData.warningColor);
    root.style.setProperty('--error-color', themeData.errorColor);
    root.style.setProperty('--info-color', themeData.infoColor);
    root.style.setProperty('--shadow-color', themeData.shadowColor);
    root.style.setProperty('--hover-background', themeData.hoverBackgroundColor);
    
    // Re-enable transitions after a brief moment
    if (instant) {
      setTimeout(() => {
        root.style.removeProperty('transition');
      }, 50);
    }

    // Inject dynamic style to force input/filter colors
    let styleElement = document.getElementById('theme-input-styles');
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'theme-input-styles';
      document.head.appendChild(styleElement);
    }
    
    styleElement.textContent = `
      /* Input fields - exclude sidebar */
      input:not(.theme-sidebar input):not(.theme-sidebar *), 
      textarea:not(.theme-sidebar textarea):not(.theme-sidebar *), 
      select:not(.theme-sidebar select):not(.theme-sidebar *), 
      button[role="combobox"]:not(.theme-sidebar button):not(.theme-sidebar *) {
        color: ${themeData.inputTextColor} !important;
      }
      
      /* Placeholders - exclude sidebar, use filter text color for consistency */
      input:not(.theme-sidebar input):not(.theme-sidebar *)::placeholder, 
      textarea:not(.theme-sidebar textarea):not(.theme-sidebar *)::placeholder {
        color: ${themeData.filterTextColor} !important;
        opacity: 0.6 !important;
      }
      
      /* Filter dropdowns and multi-select triggers - exclude sidebar */
      select:not(.theme-sidebar select):not(.theme-sidebar *), 
      select:not(.theme-sidebar select):not(.theme-sidebar *) option, 
      [role="option"]:not(.theme-sidebar [role="option"]):not(.theme-sidebar *), 
      button[role="combobox"]:not(.theme-sidebar button):not(.theme-sidebar *), 
      [data-radix-select-trigger]:not(.theme-sidebar *):not(.theme-sidebar [data-radix-select-trigger]), 
      [data-radix-select-item]:not(.theme-sidebar *):not(.theme-sidebar [data-radix-select-item]),
      [data-testid="view-toggle"]:not(.theme-sidebar *) {
        color: ${themeData.filterTextColor} !important;
      }
      
      /* Filter labels - exclude sidebar */
      label:not(.theme-sidebar label):not(.theme-sidebar *), 
      .filter-label:not(.theme-sidebar .filter-label):not(.theme-sidebar *) {
        color: ${themeData.filterLabelColor} !important;
      }
      
      /* Sidebar menu items - HIGHEST SPECIFICITY */
      .theme-sidebar .sidebar-nav-item,
      .theme-sidebar .sidebar-nav-item * {
        color: ${themeData.sidebarTextColor} !important;
      }
      
      .theme-sidebar .sidebar-nav-item svg,
      .theme-sidebar .sidebar-nav-item .lucide,
      .theme-sidebar .sidebar-nav-item [class*="lucide"] {
        color: ${themeData.sidebarIconColor} !important;
      }
      
      .theme-sidebar .sidebar-nav-item.active,
      .theme-sidebar .sidebar-nav-item.active * {
        color: ${themeData.sidebarActiveTextColor} !important;
      }
      
      .theme-sidebar .sidebar-nav-item.active svg,
      .theme-sidebar .sidebar-nav-item.active .lucide,
      .theme-sidebar .sidebar-nav-item.active [class*="lucide"] {
        color: ${themeData.sidebarActiveIconColor} !important;
      }
      
      /* Sidebar Help & Support and Sign Out buttons */
      .theme-sidebar button:not(.sidebar-nav-item),
      .theme-sidebar button:not(.sidebar-nav-item) * {
        color: ${themeData.sidebarTextColor} !important;
      }
      
      .theme-sidebar button svg,
      .theme-sidebar button .lucide,
      .theme-sidebar button [class*="lucide"] {
        color: ${themeData.sidebarIconColor} !important;
      }
    `;
  };

  const value = {
    theme,
    setTheme,
    saveTheme,
    resetTheme,
    loading,
    defaultTheme,
    getCurrentPlatformName
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};